import { OptionI } from '@/utils/forms/optionBuilders'

/**
 * Handles selection change, passing full option object(s) to form.
 */
export const handleSelectChange = (
  selected: OptionI | OptionI[] | null,
  isMulti: boolean,
  onChange: (value: OptionI | OptionI[] | null) => void
): void => {
  if (isMulti) {
    onChange(Array.isArray(selected) ? selected : [])
  } else {
    onChange(selected as OptionI | null)
  }
}

/**
 * Handles chip removal from selection.
 */
export const handleChipRemove = (
  valueToRemove: string,
  currentValue: OptionI | OptionI[] | null | undefined,
  isMulti: boolean,
  onChange: (value: OptionI | OptionI[] | null) => void
): void => {
  if (isMulti && Array.isArray(currentValue)) {
    onChange(currentValue.filter((opt) => opt.value !== valueToRemove))
  } else {
    onChange(null)
  }
}

/**
 * Handles Select All functionality for filtered options.
 * Toggles selection of visible/filtered options while preserving other selections.
 */
export const handleSelectAll = (
  filteredOptions: readonly OptionI[],
  currentValue: OptionI | OptionI[] | null | undefined,
  onChange: (value: OptionI[]) => void
): void => {
  const currentSelection = Array.isArray(currentValue) ? currentValue : []
  const selectedValues = new Set(currentSelection.map((v) => v.value))

  // Disabled options can't be selected individually, so Select All must not
  // sweep them in either.
  const selectableOptions = filteredOptions.filter(
    (opt) => !(opt as OptionI & { isDisabled?: boolean }).isDisabled
  )

  // Nothing selectable: bail out rather than letting the vacuously-true
  // every() below report "all selected" and no-op through the deselect branch
  if (selectableOptions.length === 0) {
    return
  }

  // Check if all selectable filtered options are already selected
  const isAllFilteredSelected = selectableOptions.every((opt) =>
    selectedValues.has(opt.value)
  )

  if (isAllFilteredSelected) {
    // Deselect only the filtered options, keep others selected
    const remainingOptions = currentSelection.filter(
      (v) => !selectableOptions.some((f) => f.value === v.value)
    )
    onChange(remainingOptions)
  } else {
    // Add filtered options to current selection (avoid duplicates)
    const newSelection = [
      ...currentSelection,
      ...selectableOptions.filter((f) => !selectedValues.has(f.value)),
    ]
    onChange(newSelection)
  }
}
